import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Badge,
  Grid,
  Collapse,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  LinearProgress,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Timer as TimerIcon,
  People as PeopleIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Campaign as BroadcastIcon,
  Help as HelpQueueIcon,
  ArrowBack as BackIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  VisibilityOffOutlined as EyeSlashIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Lock as LockIcon,
  Close as CloseIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxBlankIcon,
  DesktopWindows as ScreenIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { progressService } from '../../services/progress.service';
import { sessionService } from '../../services/session.service';
import { socketService, WsSessionChatMessage } from '../../services/socket.service';
import { stepService } from '../../services/step.service';
import { guacamoleService } from '../../services/guacamole.service';
import { Step } from '../../types/workshop.types';
import { DashboardData, DashboardStudent, StepStatus } from '../../types/dashboard.types';
import { useSessionSocket } from '../../hooks/useSessionSocket';
import { useNotification } from '../../context/NotificationContext';
import ReactionFeed, { useReactionCollector } from '../../components/instructor/ReactionFeed';
import PresenceBar from '../../components/instructor/PresenceBar';
import WhisperChat from '../../components/instructor/WhisperChat';

const BEHIND_THRESHOLD_POINTS = 15;

type FilterType = 'all' | 'needs_help' | 'completed' | 'behind';

const LiveConsole: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [highlightedStudent, setHighlightedStudent] = useState<string | null>(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [sessionPaused, setSessionPaused] = useState(false);
  const [workshopSteps, setWorkshopSteps] = useState<Step[]>([]);
  const [instructorNotesExpanded, setInstructorNotesExpanded] = useState(false);

  // Student detail drawer
  const [drawerStudent, setDrawerStudent] = useState<DashboardStudent | null>(null);
  const [drawerChecklistData, setDrawerChecklistData] = useState<Array<{ stepId: string; stepTitle: string; orderIndex: number; status: string; completionCriteria: string; checklistItems: Array<{ id: string; text: string; checked: boolean }> }> | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Student screen viewer
  const [screenViewOpen, setScreenViewOpen] = useState(false);
  const [screenViewUrl, setScreenViewUrl] = useState('');
  const [screenViewStudent, setScreenViewStudent] = useState('');
  const [screenViewLoading, setScreenViewLoading] = useState(false);

  // Reaction feed
  const { reactions, addReaction } = useReactionCollector();

  // Session chat state
  const [chatMessages, setChatMessages] = useState<WsSessionChatMessage[]>([]);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatUnread, setChatUnread] = useState(0);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Live timer
  const [elapsed, setElapsed] = useState('00:00:00');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadDashboard = useCallback(async () => {
    if (!id) return;
    try {
      const result = await progressService.getDashboardData(id);
      setData(result);
      setSessionPaused(result.session.status === 'paused');
      // Load workshop steps for instructor notes
      try {
        const session = await sessionService.getSession(id);
        if (session.workshopId) {
          const steps = await stepService.getSteps(session.workshopId);
          setWorkshopSteps(steps);
        }
      } catch {
        // Non-critical — instructor notes just won't show
      }
    } catch {
      showNotificationRef.current('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Live timer effect
  useEffect(() => {
    if (!data?.session.actualStart) return;
    const start = new Date(data.session.actualStart).getTime();

    // If session has ended, show static final duration
    if (data.session.endedAt) {
      const end = new Date(data.session.endedAt).getTime();
      const diff = Math.max(0, Math.floor((end - start) / 1000));
      const h = String(Math.floor(diff / 3600)).padStart(2, '0');
      const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
      const s = String(diff % 60).padStart(2, '0');
      setElapsed(`${h}:${m}:${s}`);
      return;
    }

    const tick = () => {
      const diff = Math.max(0, Math.floor((Date.now() - start) / 1000));
      const h = String(Math.floor(diff / 3600)).padStart(2, '0');
      const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
      const s = String(diff % 60).padStart(2, '0');
      setElapsed(`${h}:${m}:${s}`);
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [data?.session.actualStart, data?.session.endedAt]);

  // WebSocket real-time updates
  const { connectionStatus } = useSessionSocket({
    sessionId: id || '',
    onProgressUpdate: (event) => {
      setData((prev) => {
        if (!prev) return prev;
        const updatedStudents = prev.students.map((student) => {
          if (student.studentId !== event.studentId) return student;
          const updatedSteps = student.steps.map((step) =>
            step.stepId === event.stepId
              ? {
                  ...step,
                  status: event.status as StepStatus['status'],
                  ...(event.checklistChecked !== undefined && { checklistChecked: event.checklistChecked }),
                }
              : step,
          );
          const completedSteps = updatedSteps.filter((s) => s.status === 'completed').length;
          const progressPercent = student.totalSteps > 0 ? Math.round((completedSteps / student.totalSteps) * 100) : 0;
          const newStatus: DashboardStudent['status'] = completedSteps === student.totalSteps && student.totalSteps > 0
            ? 'completed'
            : student.hasOpenHelpRequest ? 'needs_help' : 'working';
          return {
            ...student,
            steps: updatedSteps,
            completedSteps,
            progressPercent,
            status: newStatus,
          };
        });
        const completedCount = updatedStudents.filter((s) => s.status === 'completed').length;
        const avgProgress = updatedStudents.length > 0
          ? Math.round(updatedStudents.reduce((sum, s) => sum + s.progressPercent, 0) / updatedStudents.length)
          : 0;
        const onTrackCount = updatedStudents.filter((s) => s.status === 'completed' || s.status === 'working').length;
        return {
          ...prev,
          students: updatedStudents,
          stats: { ...prev.stats, completedCount, avgProgress, onTrackCount },
        };
      });
      setHighlightedStudent(event.studentId);
      setTimeout(() => setHighlightedStudent(null), 2000);
    },
    onHelpRequest: (event) => {
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          students: prev.students.map((s) =>
            s.studentId === event.studentId ? { ...s, hasOpenHelpRequest: true, status: 'needs_help' as const } : s,
          ),
          stats: { ...prev.stats, openHelpRequests: prev.stats.openHelpRequests + 1 },
        };
      });
      setHighlightedStudent(event.studentId);
      setTimeout(() => setHighlightedStudent(null), 2000);
    },
    onHelpResolved: (event) => {
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          students: prev.students.map((s) =>
            s.studentId === event.studentId
              ? { ...s, hasOpenHelpRequest: false, status: (s.completedSteps === s.totalSteps ? 'completed' : 'working') as DashboardStudent['status'] }
              : s,
          ),
          stats: { ...prev.stats, openHelpRequests: Math.max(0, prev.stats.openHelpRequests - 1) },
        };
      });
    },
    onSessionReaction: addReaction,
    onSessionChatMessage: (data) => {
      setChatMessages(prev => [...prev, data]);
      if (!chatDrawerOpen) setChatUnread(prev => prev + 1);
    },
  });

  // Auto-refresh fallback if disconnected
  useEffect(() => {
    const interval = setInterval(() => {
      if (connectionStatus !== 'connected') {
        loadDashboard();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [connectionStatus, loadDashboard]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatDrawerOpen && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, chatDrawerOpen]);

  // Clear unread when drawer opens
  useEffect(() => {
    if (chatDrawerOpen) setChatUnread(0);
  }, [chatDrawerOpen]);

  const handlePauseResume = async () => {
    if (!id) return;
    try {
      if (sessionPaused) {
        await sessionService.startSession(id);
        setSessionPaused(false);
        showNotification('Session resumed', 'success');
      } else {
        await sessionService.pauseSession(id);
        setSessionPaused(true);
        showNotification('Session paused', 'success');
      }
    } catch {
      showNotification('Failed to update session status', 'error');
    }
  };

  const handleSendBroadcast = () => {
    if (!id || !broadcastMsg.trim()) return;
    // FM6: Check connection before sending
    if (connectionStatus !== 'connected') {
      showNotification('Cannot broadcast — not connected to session', 'warning');
      return;
    }
    socketService.sendBroadcast(id, broadcastMsg.trim());
    setBroadcastOpen(false);
    setBroadcastMsg('');
    showNotification('Message broadcast to all students', 'success');
  };

  const getFilteredStudents = (): DashboardStudent[] => {
    if (!data) return [];
    switch (filter) {
      case 'needs_help':
        return data.students.filter((s) => s.hasOpenHelpRequest);
      case 'completed':
        return data.students.filter((s) => s.status === 'completed');
      case 'behind':
        return data.students.filter((s) => s.status === 'not_started' || (s.status === 'working' && s.progressPercent < (data.stats.avgProgress - BEHIND_THRESHOLD_POINTS)));
      default:
        return data.students;
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const avatarColors = ['primary.main', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#00838f', '#4527a0', 'success.main'];
  const getAvatarColor = (index: number) => avatarColors[index % avatarColors.length];

  const handleViewScreen = async (student: DashboardStudent) => {
    if (!id) return;
    setScreenViewLoading(true);
    setScreenViewStudent(student.studentName || 'Student');
    try {
      const result = await guacamoleService.getStudentScreen(id, student.studentId);
      if (result.available && result.embedUrl) {
        setScreenViewUrl(result.embedUrl);
        setScreenViewOpen(true);
      } else {
        showNotification('No desktop available for this student', 'warning');
      }
    } catch {
      showNotification('Could not load student screen', 'error');
    } finally {
      setScreenViewLoading(false);
    }
  };

  const handleStudentRowClick = async (student: DashboardStudent) => {
    setDrawerStudent(student);
    setDrawerChecklistData(null);
    setDrawerLoading(true);
    try {
      const detail = await progressService.getStudentChecklistDetail(id!, student.studentId);
      setDrawerChecklistData(detail.steps);
    } catch {
      // Non-critical — drawer still shows step-level info from cached data
    } finally {
      setDrawerLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">Failed to load dashboard data.</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2 }}>Go Back</Button>
      </Box>
    );
  }

  const filteredStudents = getFilteredStudents();
  const isLive = data.session.status === 'active' && !sessionPaused;
  const isSessionCompleted = data.session.status === 'completed';

  return (
    <Box>
      {/* Session Header */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button size="small" startIcon={<BackIcon />} onClick={() => navigate(`/instructor/session/${id}`)} sx={{ color: 'text.secondary' }}>
              Back
            </Button>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {data.session.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {data.session.workshopTitle}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Connection indicator */}
            <Chip
              icon={connectionStatus === 'connected' ? <WifiIcon sx={{ fontSize: 14 }} /> : <WifiOffIcon sx={{ fontSize: 14 }} />}
              label={connectionStatus === 'connected' ? 'Live' : 'Offline'}
              size="small"
              color={connectionStatus === 'connected' ? 'success' : 'default'}
              variant="outlined"
              sx={{ height: 24 }}
            />
            {/* LIVE indicator */}
            {isLive && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: '#fce4ec',
                  px: 2,
                  py: 0.5,
                  borderRadius: 5,
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    bgcolor: '#d32f2f',
                    borderRadius: '50%',
                    animation: 'pulse 2s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.5, transform: 'scale(1.1)' },
                    },
                  }}
                />
                <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 600, fontSize: 13 }}>
                  LIVE SESSION
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Meta row */}
        <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              Started: {data.session.actualStart ? dayjs(data.session.actualStart).format('MMM D, YYYY [at] h:mm A') : 'Not started'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimerIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            {(() => {
              const plannedMin = data.session.duration ?? 0;
              const elapsedParts = elapsed.split(':').map(Number);
              const elapsedMin = (elapsedParts[0] || 0) * 60 + (elapsedParts[1] || 0);
              const isOvertime = plannedMin > 0 && elapsedMin >= plannedMin;
              return (
                <Typography variant="body2" sx={{ color: isOvertime ? 'warning.main' : 'text.secondary' }}>
                  Duration:{' '}
                  <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {elapsed}
                  </span>
                  {plannedMin > 0 && <> / {plannedMin} min</>}
                  {isOvertime && (
                    <span style={{ fontWeight: 600 }}> (overtime)</span>
                  )}
                </Typography>
              );
            })()}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {data.stats.totalStudents} students
            </Typography>
          </Box>
        </Box>

        {/* Controls row */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <ReactionFeed reactions={reactions} />
          <Tooltip title={isSessionCompleted ? 'Session has ended' : ''}>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<BroadcastIcon />}
                onClick={() => setBroadcastOpen(true)}
                disabled={isSessionCompleted}
              >
                Broadcast Message
              </Button>
            </span>
          </Tooltip>
          <Badge badgeContent={chatUnread} color="info" invisible={chatUnread === 0}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ChatBubbleOutlineIcon />}
              onClick={() => {
                setChatDrawerOpen(prev => !prev);
                setChatUnread(0);
              }}
            >
              Session Chat
            </Button>
          </Badge>
          <Tooltip title={isSessionCompleted ? 'Session has ended' : ''}>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={sessionPaused ? <PlayIcon /> : <PauseIcon />}
                onClick={handlePauseResume}
                disabled={isSessionCompleted}
              >
                {sessionPaused ? 'Resume Session' : 'Pause Session'}
              </Button>
            </span>
          </Tooltip>
          <Badge badgeContent={data.stats.openHelpRequests} color="error">
            <Button
              variant="contained"
              size="small"
              startIcon={<HelpQueueIcon />}
              onClick={() => navigate(`/instructor/session/${id}/help`)}
            >
              Help Queue
            </Button>
          </Badge>
        </Box>
      </Paper>

      {/* Presence Bar */}
      <PresenceBar sessionId={id!} currentPage="live-console" />

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper elevation={1} sx={{ p: 2.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: 13 }}>Average Progress</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
              {data.stats.avgProgress}%
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper elevation={1} sx={{ p: 2.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: 13 }}>Completed</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
              {data.stats.completedCount}/{data.stats.totalStudents}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper elevation={1} sx={{ p: 2.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: 13 }}>Help Requests</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: data.stats.openHelpRequests > 0 ? '#d32f2f' : 'text.primary' }}>
              {data.stats.openHelpRequests}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Paper elevation={1} sx={{ p: 2.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: 13 }}>On Track</Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
              {data.stats.onTrackCount}/{data.stats.totalStudents}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Progress Table */}
      <Paper elevation={1} sx={{ p: 3 }}>
        {/* Table header with filters */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Student Progress Grid
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {([
              { key: 'all', label: 'All Students' },
              { key: 'needs_help', label: 'Need Help' },
              { key: 'completed', label: 'Completed' },
              { key: 'behind', label: 'Behind Schedule' },
            ] as { key: FilterType; label: string }[]).map((f) => (
              <Chip
                key={f.key}
                label={f.label}
                size="small"
                variant={filter === f.key ? 'filled' : 'outlined'}
                color={filter === f.key ? 'primary' : 'default'}
                onClick={() => setFilter(f.key)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Step Progress (1-{data.session.totalSteps})
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Current Step</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Completion</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Time Spent</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStudents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No students match this filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filteredStudents.map((student, idx) => (
                <TableRow
                  key={student.studentId}
                  onClick={() => handleStudentRowClick(student)}
                  sx={{
                    transition: 'background-color 0.3s ease',
                    bgcolor: highlightedStudent === student.studentId ? '#fff9c4' : 'transparent',
                    '&:hover': { bgcolor: highlightedStudent === student.studentId ? '#fff9c4' : 'grey.50' },
                    cursor: 'pointer',
                  }}
                >
                  {/* Student cell */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: getAvatarColor(idx), fontSize: 14, fontWeight: 600 }}>
                        {getInitials(student.studentName || 'S')}
                      </Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {student.studentName || 'Unknown'}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Step dots */}
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      {student.steps.map((step) => {
                        const isChecklistInProgress = step.checklistTotal > 0 && step.status === 'in_progress';
                        const checklistPct = step.checklistTotal > 0 ? (step.checklistChecked / step.checklistTotal) * 100 : 0;
                        const tooltipText = step.status === 'completed'
                          ? `Step ${step.orderIndex + 1}: ${step.stepTitle} ✓`
                          : step.checklistTotal > 0
                            ? `Step ${step.orderIndex + 1}: ${step.stepTitle} (${step.checklistChecked}/${step.checklistTotal} tasks)`
                            : `Step ${step.orderIndex + 1}: ${step.stepTitle}`;
                        return (
                          <Tooltip key={step.stepId} title={tooltipText} arrow placement="top">
                            {isChecklistInProgress ? (
                              <Box sx={{ position: 'relative', width: 24, height: 24, flexShrink: 0 }}>
                                {/* Background track */}
                                <CircularProgress
                                  variant="determinate"
                                  value={100}
                                  size={24}
                                  thickness={3}
                                  sx={{ color: 'grey.300', position: 'absolute', top: 0, left: 0 }}
                                />
                                {/* Progress ring */}
                                <CircularProgress
                                  variant="determinate"
                                  value={checklistPct}
                                  size={24}
                                  thickness={3}
                                  sx={{ color: 'primary.main', position: 'absolute', top: 0, left: 0 }}
                                />
                                <Box sx={{
                                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: 9, fontWeight: 700, color: 'primary.main',
                                }}>
                                  {step.orderIndex + 1}
                                </Box>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: 10,
                                  fontWeight: 600,
                                  flexShrink: 0,
                                  ...(step.status === 'completed'
                                    ? { bgcolor: '#4caf50', color: '#fff' }
                                    : step.status === 'in_progress'
                                      ? { bgcolor: 'primary.main', color: '#fff', boxShadow: (theme: any) => `0 0 0 3px ${theme.palette.primary.main}4D` }
                                      : { bgcolor: 'divider', color: '#9e9e9e' }),
                                }}
                              >
                                {step.status === 'completed' ? '✓' : step.orderIndex + 1}
                              </Box>
                            )}
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </TableCell>

                  {/* Current step */}
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {student.status === 'completed'
                        ? 'Done'
                        : student.currentStepIndex !== null
                          ? `Step ${student.currentStepIndex + 1} of ${student.totalSteps}`
                          : student.completedSteps > 0
                            ? `Step ${student.completedSteps + 1} of ${student.totalSteps}`
                            : 'Not started'}
                    </Typography>
                  </TableCell>

                  {/* Completion badge */}
                  <TableCell>
                    <Chip
                      label={`${student.progressPercent}%`}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: 12,
                        bgcolor: student.progressPercent === 100 ? '#e8f5e9' : '#e3f2fd',
                        color: student.progressPercent === 100 ? '#4caf50' : 'primary.main',
                      }}
                    />
                  </TableCell>

                  {/* Time */}
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                      {student.timeSpentMinutes} min
                    </Typography>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <StatusChip status={student.status} hasHelp={student.hasOpenHelpRequest} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Instructor Notes Section */}
      {workshopSteps.some((s) => s.instructorNotes) && (
        <Paper elevation={1} sx={{ p: 3, mt: 3 }}>
          <Box
            onClick={() => setInstructorNotesExpanded(!instructorNotesExpanded)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <EyeSlashIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Instructor Notes
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.625rem' }}>
              Not visible to students
            </Typography>
            <Box sx={{ flex: 1 }} />
            {instructorNotesExpanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
          </Box>
          <Collapse in={instructorNotesExpanded}>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {workshopSteps
                .filter((s) => s.instructorNotes)
                .map((step) => (
                  <Box key={step.id} sx={{ pl: 1, borderLeft: 2, borderColor: 'primary.light' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Step {step.orderIndex + 1}: {step.title}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary', fontSize: '0.8125rem' }}>
                      {step.instructorNotes}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Collapse>
        </Paper>
      )}

      {/* Broadcast Dialog */}
      <Dialog open={broadcastOpen} onClose={() => setBroadcastOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Broadcast Message to All Students</DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={4}
            fullWidth
            label="Message"
            placeholder="e.g., Take a 5 minute break, we'll resume shortly..."
            value={broadcastMsg}
            onChange={(e) => setBroadcastMsg(e.target.value.slice(0, 500))}
            helperText={`${broadcastMsg.length}/500`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBroadcastOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendBroadcast} disabled={!broadcastMsg.trim()}>
            Send to All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Student Detail Drawer */}
      <Drawer
        anchor="right"
        open={!!drawerStudent}
        onClose={() => setDrawerStudent(null)}
        PaperProps={{ sx: { width: 420, maxWidth: '90vw' } }}
      >
        {drawerStudent && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Drawer header */}
            <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {drawerStudent.studentName}
                </Typography>
                <IconButton size="small" onClick={() => setDrawerStudent(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {drawerStudent.studentEmail}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <StatusChip status={drawerStudent.status} hasHelp={drawerStudent.hasOpenHelpRequest} />
                <Chip label={`${drawerStudent.progressPercent}% done`} size="small" sx={{ fontWeight: 600, bgcolor: '#e3f2fd', color: 'primary.main' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {drawerStudent.timeSpentMinutes} min
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ScreenIcon />}
                disabled={screenViewLoading}
                onClick={(e) => { e.stopPropagation(); handleViewScreen(drawerStudent); }}
                sx={{ mt: 1.5 }}
              >
                {screenViewLoading ? 'Loading...' : 'View Screen'}
              </Button>
            </Box>

            {/* Loading indicator */}
            {drawerLoading && <LinearProgress />}

            {/* Step list */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 0 }}>
              <List disablePadding>
                {(drawerChecklistData || drawerStudent.steps.map((s) => ({
                  stepId: s.stepId,
                  stepTitle: s.stepTitle,
                  orderIndex: s.orderIndex,
                  status: s.status,
                  completionCriteria: s.completionCriteria || 'manual',
                  checklistItems: [] as Array<{ id: string; text: string; checked: boolean }>,
                }))).map((step, i) => {
                  const isActive = step.status === 'in_progress';
                  const isCompleted = step.status === 'completed';
                  const isLocked = step.status === 'not_started' && i > 0;
                  return (
                    <React.Fragment key={step.stepId}>
                      {i > 0 && <Divider />}
                      <ListItem sx={{ px: 2.5, py: 1.5, alignItems: 'flex-start', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: step.checklistItems.length > 0 ? 1 : 0 }}>
                          {isCompleted ? (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                          ) : isActive ? (
                            <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fff' }} />
                            </Box>
                          ) : isLocked ? (
                            <LockIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                          ) : (
                            <UncheckedIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: isActive ? 600 : 400, flex: 1, color: isLocked ? 'text.disabled' : 'text.primary' }}>
                            Step {step.orderIndex + 1}: {step.stepTitle}
                          </Typography>
                          {isCompleted && (
                            <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>✓</Typography>
                          )}
                          {isActive && (
                            <Chip label="active" size="small" color="primary" sx={{ height: 20, fontSize: 11 }} />
                          )}
                          {isLocked && (
                            <Typography variant="caption" color="text.disabled">locked</Typography>
                          )}
                        </Box>

                        {/* Checklist items (only for checklist_gated steps with items) */}
                        {step.checklistItems.length > 0 && (
                          <Box sx={{ ml: 3.5, width: 'calc(100% - 28px)' }}>
                            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                              {step.checklistItems.map((item) => (
                                <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                                  {item.checked ? (
                                    <CheckBoxIcon sx={{ color: 'success.main', fontSize: 18 }} />
                                  ) : (
                                    <CheckBoxBlankIcon sx={{ color: 'grey.400', fontSize: 18 }} />
                                  )}
                                  <Typography variant="body2" sx={{ fontSize: 13, color: item.checked ? 'text.secondary' : 'text.primary', textDecoration: item.checked ? 'line-through' : 'none' }}>
                                    {item.text}
                                  </Typography>
                                </Box>
                              ))}
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="caption" color="text.secondary">
                                {step.checklistItems.filter((i) => i.checked).length} of {step.checklistItems.length} tasks complete
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Student Screen Viewer Dialog */}
      <Dialog
        open={screenViewOpen}
        onClose={() => { setScreenViewOpen(false); setScreenViewUrl(''); }}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { width: '90vw', height: '85vh', maxWidth: '1400px' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScreenIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {screenViewStudent}&apos;s Screen
            </Typography>
            <Chip label="LIVE" size="small" color="error" sx={{ fontWeight: 700, fontSize: 11 }} />
          </Box>
          <IconButton onClick={() => { setScreenViewOpen(false); setScreenViewUrl(''); }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box sx={{ flex: 1, overflow: 'hidden', bgcolor: '#1e1e2e' }}>
          {screenViewUrl && (
            <iframe
              src={screenViewUrl}
              title={`${screenViewStudent}'s desktop`}
              style={{ width: '100%', height: '100%', border: 'none' }}
              sandbox="allow-scripts allow-same-origin allow-forms"
            />
          )}
        </Box>
      </Dialog>

      {/* Whisper Chat */}
      <WhisperChat sessionId={id!} />

      {/* Session Chat — Floating Panel */}
      {chatDrawerOpen && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 340,
            height: 440,
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 1300,
            animation: 'chatSlideUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            '@keyframes chatSlideUp': {
              '0%': { opacity: 0, transform: 'scale(0.92) translateY(12px)' },
              '100%': { opacity: 1, transform: 'scale(1) translateY(0)' },
            },
          }}
        >
          {/* Top accent gradient */}
          <Box sx={{ height: 3, background: 'linear-gradient(90deg, #42a5f5, #7c4dff, #e040fb)', flexShrink: 0 }} />

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: '8px', background: 'linear-gradient(135deg, #42a5f5, #7c4dff)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: 'white' }} />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Session Chat</Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                  Live session messages
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={() => setChatDrawerOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages feed */}
          <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5, p: 2 }}>
            {chatMessages.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 6, fontStyle: 'italic' }}>
                No messages yet
              </Typography>
            )}
            {chatMessages.map((msg, i) => (
              <Box
                key={msg.timestamp + '-' + i}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: msg.userRole === 'instructor' ? 'flex-end' : 'flex-start',
                  maxWidth: '88%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...(msg.userRole === 'instructor' && { justifyContent: 'flex-end' }) }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: msg.userRole === 'instructor' ? 'primary.main' : 'text.primary', fontSize: '0.68rem' }}>
                    {msg.userName}
                  </Typography>
                  {msg.userRole === 'instructor' && (
                    <Chip label="Host" size="small" color="primary" sx={{ height: 14, fontSize: '0.55rem', fontWeight: 700 }} />
                  )}
                </Box>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderRadius: '12px',
                    ...(msg.userRole === 'instructor'
                      ? { background: 'linear-gradient(135deg, #e3f2fd, #ede7f6)', borderBottomRightRadius: '4px' }
                      : { bgcolor: 'grey.100', borderBottomLeftRadius: '4px' }),
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.81rem', lineHeight: 1.5 }}>{msg.message}</Typography>
                </Box>
              </Box>
            ))}
            <div ref={chatEndRef} />
          </Box>

          {/* Input area */}
          <Box sx={{ display: 'flex', gap: 1, p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fafafa' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Type a message..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && chatInput.trim()) {
                  socketService.sendChatMessage(id!, chatInput.trim());
                  setChatInput('');
                }
              }}
              inputProps={{ maxLength: 500 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            />
            <IconButton
              size="small"
              disabled={!chatInput.trim()}
              onClick={() => {
                if (chatInput.trim()) {
                  socketService.sendChatMessage(id!, chatInput.trim());
                  setChatInput('');
                }
              }}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #42a5f5, #7c4dff)',
                color: 'white',
                '&:hover': { background: 'linear-gradient(135deg, #1e88e5, #651fff)' },
                '&.Mui-disabled': { background: 'grey.300', color: 'grey.500' },
              }}
            >
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

// Status chip component
const StatusChip: React.FC<{ status: string; hasHelp: boolean }> = ({ status, hasHelp }) => {
  if (status === 'completed') {
    return <Chip label="Completed" size="small" color="success" />;
  }
  if (status === 'needs_help' || hasHelp) {
    return (
      <Chip
        label="Needs Help"
        size="small"
        color="error"
        sx={{
          animation: 'chipPulse 2s ease-in-out infinite',
          '@keyframes chipPulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.7 },
          },
        }}
      />
    );
  }
  if (status === 'working') {
    return <Chip label="Working" size="small" color="primary" />;
  }
  return <Chip label="Not Started" size="small" variant="outlined" />;
};

export default LiveConsole;
